import {Component, Inject, Input} from '@angular/core';
import {copyToClipboard} from "@webmate-web-core/shared/utils/clipboard";
import {WmNotificationService} from "@webmate-web-core/shared/components/notification/wm-notification.service";
import {map, mergeMap} from "rxjs/operators";
import {Observable} from "rxjs";
import {ReportSharingToken} from "../../../../../report/types";
import {ReportService} from "@webmate-web-core/shared/services/report.service";
import {DateTime} from "@webmate-web-core/shared/types/base-types";

import {WebmateToolConfig} from "@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service";
import {ProjectId} from "@webmate-web-core/shared/types/ids";
import {TOOL_CONTAINER_DATA} from "@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component";
import {EditReportToolConfig} from "@webmate-web-core/projects/project/auditlab/audits/edit-report-tool.component";
import {FormsModule} from "@angular/forms";
import {InputTextModule} from "primeng/inputtext";
import {FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {TooltipModule} from "primeng/tooltip";
import {ButtonModule} from "primeng/button";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {faQuestionCircle} from "@fortawesome/pro-solid-svg-icons";
import {Router} from "@angular/router";
import {ReportId} from "../../../../../report/report_types";
import {NgClass} from '@angular/common';


export class ReportSharingToolConfig implements WebmateToolConfig {
    scrollable = true;
    constructor(public reportId: ReportId, public projectId: ProjectId, public toolName: string = 'Share Report') {}
}


@Component({
    styleUrls: ['./report-sharing-tool.component.less'],
    templateUrl: './report-sharing-tool.component.html',
    standalone: true,
    imports: [FormsModule, InputTextModule, FontAwesomeModule, TooltipModule, ButtonModule, ProgressSpinnerModule, NgClass]
})
export class ReportSharingToolComponent {

    sharingTokens: ReportSharingToken[] = [];
    shareLink?: string = undefined;
    creatingNewToken: boolean = false;
    name: string = "";
    idToBeDeleted?: string = undefined;
    copied: boolean = false;
    loading: boolean = false;
    reportId: ReportId;

    constructor(private reportService: ReportService, private notificationService: WmNotificationService, private router: Router,
                @Inject(TOOL_CONTAINER_DATA) private config: ReportSharingToolConfig) {
        this.reportId = config.reportId;
    }

    ngOnInit(){
        this.getReportSharingTokens().subscribe();
    }

    makeShareLinkFromToken(token: string): string {
        const link = this.router.createUrlTree(['report', 'shared', token]).toString()
        return window.location.origin + link;
    }

    createSharingToken(name: string, expirationTime?: DateTime): void {
        this.creatingNewToken = true;
        this.reportService.createNewSharingToken$(this.reportId, name, expirationTime).pipe(mergeMap(next => {
            this.creatingNewToken = false;
            this.shareLink = this.makeShareLinkFromToken(next.token);
            return this.getReportSharingTokens();
        })).subscribe();
    }

    getReportSharingTokens(): Observable<void> {
        this.loading = true;
        return this.reportService.getReportSharingTokens$(this.reportId).pipe(map(tokenInfos => {
            this.sharingTokens = tokenInfos.sort((a: ReportSharingToken, b: ReportSharingToken) => a.name.localeCompare(b.name) || a.creationTime.localeCompare(b.creationTime));
            if (this.idToBeDeleted && this.sharingTokens.filter(apiKey => apiKey.id === this.idToBeDeleted).length === 0) {
                this.idToBeDeleted = undefined;
            }
            this.loading = false;
        }));
    }

    deleteSharingToken(id: string): void {
        let name: string = this.sharingTokens.filter(apiKey => apiKey.id === id)[0]?.name;
        if (name === null) { // If there is no key with the given ID. Unreachable in practice.
            return;
        }
        this.notificationService.open(`You are about to delete the API key "${name}". Are you sure you want to proceed?`, "Yes, delete", () => {
            this.idToBeDeleted = id;
            this.reportService.deleteSharingToken$(this.reportId, id).pipe(mergeMap(() => this.getReportSharingTokens())).subscribe();
        });
    }

    copyToken(): void {
        if (!this.shareLink) {
            return;
        }

        copyToClipboard(this.shareLink).then(() => {
            this.copied = true;
            setTimeout(() => this.copied = false, 1500);
        });
    }

    protected readonly faQuestionCircle = faQuestionCircle;
}
